<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createFtpConfig } from "./fs";
  import { makeFileSystemFtp, testFtpConnection } from "./fs-ftp";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createFtpConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(config.host.trim() != "" && !testingConnection);
  let defaultPort = $derived(config.tls == "implicit" ? "990" : "21");

  function ftpWho(): string {
    let host = config.host.includes(":") ? `[${config.host}]` : config.host;
    if (config.port != "" && config.port != defaultPort) {
      host += ":" + config.port;
    }
    if (config.user == "") {
      return host;
    }
    return `${config.user}@${host}`;
  }

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing ftp connection");
    try {
      config.host = config.host.trim();
      config.port = config.port.trim();
      config.user = config.user.trim();
      config.name = config.name.trim();
      await testFtpConnection(config);
      if (config.name == "") {
        config.name = ftpWho();
      }
      let fs = makeFileSystemFtp(config);
      let who = config.name;
      telegramNotify(`New FTP connection for ${who}`);
      captureEvent("ftp-login", { host: config.host, tls: config.tls });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect FTP">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-ftp-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-ftp-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <div class="mt-3 flex gap-3">
        <div class="flex grow flex-col">
          <label for="input-ftp-host">Host:</label>
          <input
            type="text"
            id="input-ftp-host"
            autocomplete="off"
            class="outline-hidden border px-2 py-1"
            bind:value={config.host}
          />
        </div>
        <div class="flex w-20 flex-col">
          <label for="input-ftp-port">Port:</label>
          <input
            type="text"
            id="input-ftp-port"
            autocomplete="off"
            class="outline-hidden border px-2 py-1"
            placeholder={defaultPort}
            bind:value={config.port}
          />
        </div>
      </div>

      <label class="mt-3" for="input-ftp-tls">Encryption:</label>
      <select id="input-ftp-tls" class="outline-hidden border px-2 py-1" bind:value={config.tls}>
        <option value="none">None</option>
        <option value="explicit">Explicit TLS (FTPES)</option>
        <option value="implicit">Implicit TLS (FTPS)</option>
      </select>

      <label class="mt-3" for="input-ftp-user">User: <span class="text-gray-400">(empty for anonymous)</span></label>
      <input
        type="text"
        id="input-ftp-user"
        autocomplete="username"
        class="outline-hidden border px-2 py-1"
        bind:value={config.user}
      />

      <label class="mt-3" for="input-ftp-password">Password:</label>
      <input
        type="password"
        id="input-ftp-password"
        autocomplete="current-password"
        class="outline-hidden border px-2 py-1"
        bind:value={config.password}
      />

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect FTP</button>
    </div>
  {/snippet}
</DialogBase>
